import React, { useState, useEffect } from 'react';
import { Box, Image, IconButton, HStack } from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { useAuth } from '../hooks/useAuth';
import { usePaywallContext } from '../contexts/PaywallContext';

interface ClubLogoProps {
  size?: number;
  variant?: 'inline' | 'fixed';
  responsive?: boolean;
}

const ClubLogo: React.FC<ClubLogoProps> = ({ 
  size = 40, 
  variant = 'inline',
  responsive = true
}) => {
  const { user } = useAuth();
  const { userSubscription } = usePaywallContext();
  
  // Initialize scaleMultiplier with localStorage value or default to largest scale
  const getInitialScaleMultiplier = () => {
    try {
      const savedScale = localStorage.getItem('clubLogoScaleMultiplier');
      if (savedScale) {
        const parsedScale = parseFloat(savedScale);
        if (!isNaN(parsedScale) && parsedScale >= 0.5 && parsedScale <= 3) {
          return parsedScale;
        }
      }
    } catch (error) {
      console.error('Error loading saved logo scale:', error);
    }
    return 3; // Default to largest scale
  };

  const [scaleMultiplier, setScaleMultiplier] = useState(getInitialScaleMultiplier);
  const [isHovered, setIsHovered] = useState(false);
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);

  // Check if user is on a Club plan
  const isClubPlan = userSubscription?.planType === 'club';

  // Save scale multiplier to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('clubLogoScaleMultiplier', scaleMultiplier.toString());
    } catch (error) {
      console.error('Error saving logo scale:', error);
    }
  }, [scaleMultiplier]);

  // Handle logo rotation - simple 3 minute intervals
  useEffect(() => {
    // Only run if user is on club plan and has logos
    if (!isClubPlan || !user?.clubLogos || user.clubLogos.length === 0) {
      return;
    }

    const logos = user.clubLogos;
    
    // If there's only one logo, don't rotate
    if (logos.length <= 1) {
      return;
    }

    // Simple interval to rotate logos every 3 minutes
    const interval = setInterval(() => {
      setCurrentLogoIndex(prevIndex => {
        const newIndex = (prevIndex + 1) % logos.length;
        return newIndex;
      });
    }, 180000);

    return () => clearInterval(interval);
  }, [isClubPlan, user?.clubLogos]);

  // Don't render if user is not on a club plan or doesn't have any club logos
  if (!isClubPlan || !user?.clubLogos || user.clubLogos.length === 0) {
    return null;
  }

  const logos = user.clubLogos;
  const currentLogo = logos[currentLogoIndex];

  // Handle size adjustments
  const handleIncreaseSize = () => {
    setScaleMultiplier(prev => Math.min(prev + 0.2, 3)); // Max 3x size
  };

  const handleDecreaseSize = () => {
    setScaleMultiplier(prev => Math.max(prev - 0.2, 0.5)); // Min 0.5x size
  };

  if (variant === 'fixed') {
    return (
      <Box
        position="fixed"
        bottom="140px"
        left="50%"
        transform="translateX(-50%)"
        zIndex={1001}
        maxWidth="120px"
        maxHeight="60px"
        overflow="hidden"
        bg="transparent"
        border="2px solid"
        borderColor="gray.200"
        boxShadow="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Image
          src={currentLogo.url}
          alt="Club Logo"
          width="100%"
          height="100%"
          objectFit="contain"
          borderRadius="md"
        />
      </Box>
    );
  }

  // Inline variant - for use within containers
  return (
    <Box
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        transition="all 0.3s ease"
        transform={`scale(${scaleMultiplier})`}
        transformOrigin="top"
        position="relative"
        bg="transparent"
        {...(responsive ? {
          // Dynamic sizing that adapts to available space
          minWidth: '40px',
          minHeight: '40px',
          maxWidth: '1600px', // Much larger max for horizontal expansion
          maxHeight: '200px',
          width: 'clamp(40px, 30vw, 1600px)', // More horizontal space
          height: 'clamp(40px, 8vw, 200px)',
        } : {
          // Fixed sizing
          boxSize: `${size}px`
        })}
      >
        <Image
          src={currentLogo.url}
          alt="Club Logo"
          width="100%"
          height="100%"
          objectFit="contain"
          borderRadius="md"
        />
      </Box>
      
      {/* Size control buttons - only show on hover */}
      {isHovered && (
        <HStack
          position="absolute"
          top="-10px"
          right="-10px"
          spacing={1}
          zIndex={1002}
        >
          <IconButton
            aria-label="Decrease logo size"
            icon={<MinusIcon />}
            size="xs"
            colorScheme="blue"
            variant="solid"
            onClick={handleDecreaseSize}
            isDisabled={scaleMultiplier <= 0.5}
            borderRadius="full"
            boxShadow="md"
          />
          <IconButton
            aria-label="Increase logo size"
            icon={<AddIcon />}
            size="xs"
            colorScheme="blue"
            variant="solid"
            onClick={handleIncreaseSize}
            isDisabled={scaleMultiplier >= 3}
            borderRadius="full"
            boxShadow="md"
          />
        </HStack>
      )}
    </Box>
  );
};

export default ClubLogo;
